import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, Button, Group, Stack } from '@mantine/core';
import type { SerializedEditorState } from 'lexical';
import { InfoIcon } from 'lucide-react';
import { type FC, useCallback, useEffect, useMemo } from 'react';
import { type SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';
import { FlowDrawerComparisonItemSelect } from '@/components/flow-drawer/components/flow-drawer-comparison-item-select';
import { FlowItemBodyEditor } from '@/components/flow-item-form/components/flow-item-body-editor';
import { FlowItemTitleEditor } from '@/components/flow-item-form/components/flow-item-title-editor';
import {
	type FlowItemFormValues,
	flowItemFormSchema
} from '@/components/flow-item-form/flow-item-form.schema';
import { generateFormDefaultValues } from '@/components/flow-item-form/flow-item-form.utils.ts';
import useFlowStore from '@/store/flow.ts';
import type { TFlowItemFrontend, TGroupItemFrontend } from '@/types/common';
import { AppEvent } from '@/types/event-emitter.types.ts';
import {
	editorValueToPlainText,
	plainTextToEditorState
} from '@/utils/editor-conversion';
import { emitter } from '@/utils/event-emitter.ts';
import styles from './flow-drawer-form.module.css';

interface FlowItemFormProps {
	type?: string;
	isEdit: boolean;
	flowItem?: Partial<TFlowItemFrontend>;
	onSubmit: (type: string, data: TGroupItemFrontend) => void;
	onCancel: () => void;
}

const FlowItemForm: FC<FlowItemFormProps> = ({
	type,
	flowItem,
	onSubmit,
	onCancel,
	isEdit
}) => {
	const { isComparingMode, cardToCompare } = useFlowStore(
		useShallow((state) => ({
			isComparingMode: state.flowDrawer.isComparingMode,
			cardToCompare: state.flowDrawer.cardToCompare,
			setCardToCompare: state.setCardToCompare
		}))
	);
	const {
		reset,
		setValue,
		handleSubmit,
		control,
		formState: { dirtyFields }
	} = useForm<FlowItemFormValues>({
		resolver: zodResolver(flowItemFormSchema),
		defaultValues: generateFormDefaultValues(flowItem)
	});

	const isReadOnly = Boolean(flowItem?.source?.is_from_library);

	const onFromSubmit: SubmitHandler<FlowItemFormValues> = (data) => {
		reset(data);
		onSubmit(type ?? flowItem?.type ?? '', data);
	};

	const handleCancel = useCallback(() => {
		reset();
		onCancel();
	}, [onCancel, reset]);

	const handleChangeBody = useCallback(
		(value: SerializedEditorState) => {
			setValue('body', value, { shouldDirty: true });
		},
		[setValue]
	);

	const handleTitleChange = useCallback(
		(value: SerializedEditorState) => {
			setValue('title', value, { shouldDirty: true });
		},
		[setValue]
	);

	const body = useWatch({ control, name: 'body' });
	const title = useWatch({ control, name: 'title' });

	const titleChangeSet = useMemo(
		() => ({
			previous: editorValueToPlainText(title ?? flowItem?.source?.title),
			current: editorValueToPlainText(cardToCompare?.title)
		}),
		[title, flowItem, cardToCompare]
	);

	const bodyChangeSet = useMemo(
		() => ({
			previous: editorValueToPlainText(body ?? flowItem?.source?.body),
			current: editorValueToPlainText(cardToCompare?.body)
		}),
		[body, flowItem, cardToCompare]
	);

	const comparisonTitleValues = useMemo(
		() => ({
			current: plainTextToEditorState(titleChangeSet.current),
			previous: plainTextToEditorState(titleChangeSet.previous)
		}),
		[titleChangeSet]
	);

	const comparisonBodyValues = useMemo(
		() => ({
			current: plainTextToEditorState(bodyChangeSet.current),
			previous: plainTextToEditorState(bodyChangeSet.previous)
		}),
		[bodyChangeSet]
	);

	useEffect(() => {
		emitter.emit(AppEvent.IS_FLOW_ITEM_FORM_CHANGED, {
			isFormChanged: Boolean(dirtyFields.title || dirtyFields.body)
		});
	}, [dirtyFields.title, dirtyFields.body]);

	return (
		<form
			className={styles.flowDrawerForm}
			onSubmit={handleSubmit(onFromSubmit)}
		>
			<Stack
				gap="lg"
				h="100%"
			>
				<Group
					align="stretch"
					flex={1}
					gap="xl"
					wrap="nowrap"
				>
					{isComparingMode && (
						<Stack
							flex={1}
							gap="lg"
						>
							<FlowDrawerComparisonItemSelect />
							<FlowItemTitleEditor
								changeSet={titleChangeSet}
								isAddedActive
								isDisabled
								onChange={handleTitleChange}
								value={comparisonTitleValues.current}
							/>
							<FlowItemBodyEditor
								changeSet={bodyChangeSet}
								isAddedActive
								isDisabled
								isEdit={isEdit}
								onChange={handleChangeBody}
								value={comparisonBodyValues.current}
							/>
						</Stack>
					)}

					<Stack
						flex={1}
						gap="lg"
					>
						{isComparingMode && (
							<Alert
								color="yellow"
								icon={<InfoIcon />}
								p={8}
								title={
									flowItem?.source?.is_from_library
										? 'This is a library item that is currently present on the canvas.'
										: 'This is a customized item that is currently present on the canvas.'
								}
								variant="light"
								w="100%"
							/>
						)}
						<FlowItemTitleEditor
							changeSet={titleChangeSet}
							isDisabled={isReadOnly || isComparingMode}
							isRemoveActive
							onChange={handleTitleChange}
							value={isComparingMode ? comparisonTitleValues.previous : title}
						/>
						<FlowItemBodyEditor
							changeSet={bodyChangeSet}
							isDisabled={isReadOnly || isComparingMode}
							isEdit={isEdit}
							isRemoveActive
							onChange={handleChangeBody}
							value={
								isComparingMode ? comparisonBodyValues.previous : (body ?? '')
							}
						/>
					</Stack>
				</Group>

				<Box
					className={styles.buttonsWrapper}
					mt="auto"
				>
					<Group>
						<Button
							color="gray"
							onClick={handleCancel}
							variant="transparent"
						>
							Cancel
						</Button>
						{flowItem?.source?.is_from_library ||
						isComparingMode ? undefined : (
							<Button
								color="dark"
								disabled={!(dirtyFields.title || dirtyFields.body)}
								type="submit"
								variant="filled"
							>
								Save
							</Button>
						)}
					</Group>
				</Box>
			</Stack>
		</form>
	);
};

export default FlowItemForm;
